import React, {useEffect, useMemo, useState} from "react";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { gettingPycodesCombination } from 'utils/PaycodesCombination';
import { ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import CachedIcon from '@mui/icons-material/Cached';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddPaycodesCombination } from 'components/dialog/pycodes_combination/AddPaycodesCombination';
import { DeletePaycodesCombination } from 'components/dialog/pycodes_combination/DeletePaycodesCombination';
import { EditPaycodesCombination } from 'components/dialog/pycodes_combination/EditPaycodesCombination';
import { InfoPaycodesCombination } from "components/dialog/pycodes_combination/InfoPaycodeCombination";
import dayjs from "dayjs";

const BASE_PAYCODE_COMBINATIONS_PATH = '/settings/paycode_combinations';

const parsePaycodeCombinationModalRoute = (pathname) => {
    const segments = pathname.split('/').filter(Boolean);
    if (segments[0] !== 'settings' || segments[1] !== 'paycode_combinations') {
        return { action: null, id: null };
    }

    const action = segments[2] || null;
    const rawId = segments[3];
    const id = rawId && /^\d+$/.test(rawId) ? Number(rawId) : null;

    return { action, id };
};

const ContextMenu = ({contextMenu, setContextMenu, selectedRow, reloading, onOpenAdd, onOpenEdit, onOpenInfo}) => {
    const { t } = useTranslation()
    const [openDelete, setOpenDelete] = useState(false)

    const options = [
        {
            name: t('settings.paycodes_combination.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                onOpenAdd()
                setContextMenu(null)
                },
            color: '',
            selected: false,
            onlyOne: false,
            permision: JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.CreatePaycodeCombination') ? true : false,
        },
        {
            name: t('settings.paycodes_combination.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                onOpenEdit()
                setContextMenu(null)
            },
            color: '',
            disabled: selectedRow.length !== 1 ? true : false,
            permision: JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.UpdatePaycodeCombination') ? true : false,
        },
        {
            name: t('settings.paycodes_combination.table.context_menu.delete'),
            icon: <DeleteIcon color='error'/>,
            click: () => {
                setOpenDelete(true)
                setContextMenu(null)
            },
            color: 'error',
            disabled: true, //selectedRow.length <= 0 ? true : false,
            selected: false,
            onlyOne: true,
            permision: JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.DeletePaycodeCombination') ? true : false,
        },
        {
            name: t('settings.paycodes_combination.table.context_menu.info'),
            icon: <InfoIcon />,
            disabled: selectedRow.length !== 1 ? true : false,
            click: () => {
                onOpenInfo()
                setContextMenu(null)
            },
            color: '',
            permision: JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.PaycodeOne') ? true : false,
        },
        {
            name: t('settings.paycodes_combination.table.context_menu.reload'),
            icon: <CachedIcon/>,
            color: '',
            click: () => {
                reloading()
                setContextMenu(null)
            },
            permision: true
        }
    ]

    return (
        <>
            <Menu
                open={contextMenu ? true : false }
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined }
                sx={{boxShadow: 'none'}}
            >
                { options.map((option, index) =>
                    <MenuItem key={index} onClick={option.click} disabled={option.disabled}>
                        <ListItemIcon>
                            { option.icon }
                        </ListItemIcon>

                        <ListItemText primary={option.name} slotProps={{ primary : { color: option.color } }}/>
                    </MenuItem>
                )}
            </Menu>

            <DeletePaycodesCombination open={openDelete} setOpenDelete={setOpenDelete} setAlert={() => {}} setOpenAlert={() => {}} selectedRow={selectedRow} setSelected={() => {}} reloading={reloading} />
        </>
    )
}

const PaycodesCombinationTable = () => {
    const { t } = useTranslation()

    const [loadingPaycodesCombination, setLoadingPaycodesCombination] = useState(true)
    const [rows, setRows] = useState([])
    const [contextMenu, setContextMenu]   = useState(null)
    const [selectedRow, setSelectedRow]   = useState([])
    const [selected, setSelected] = useState([])
    const navigate = useNavigate()
    const location = useLocation()

    const modalRoute = useMemo(() => parsePaycodeCombinationModalRoute(location.pathname), [location.pathname])
    const openAdd = modalRoute.action === 'add'
    const openEdit = modalRoute.action === 'update' && modalRoute.id !== null
    const openInfo = modalRoute.action === 'detail' && modalRoute.id !== null

    const selectedRowFromRoute = useMemo(() => {
        if (modalRoute.id === null) return null
        return rows.find((row) => Number(row.id) === modalRoute.id || Number(row.paycode_combination_id) === modalRoute.id) || null
    }, [rows, modalRoute.id])

    const selectedRowsForRoute = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow

    const closeRouteModal = () => {
        navigate(BASE_PAYCODE_COMBINATIONS_PATH)
    }

    const handleOpenAdd = () => {
        navigate(`${BASE_PAYCODE_COMBINATIONS_PATH}/add`)
    }

    const handleOpenEdit = () => {
        const current = selectedRow[0]
        if (!current?.id) return
        navigate(`${BASE_PAYCODE_COMBINATIONS_PATH}/update/${current.id}`)
    }

    const handleOpenInfo = () => {
        const current = selectedRow[0]
        if (!current?.id) return
        navigate(`${BASE_PAYCODE_COMBINATIONS_PATH}/detail/${current.id}`)
    }

    const columns = [
        {
            field: 'name',
            headerName: t('settings.paycodes_combination.table.columns.name'),
            flex: 1,
        },
        {
            field: 'description',
            headerName: t('settings.paycodes_combination.table.columns.description'),
            flex: 1,
            filterable: false
        },
        {
            field: 'start_date',
            headerName: t('settings.paycodes_combination.table.columns.start_date'),
            flex: 1,
            renderCell: (params) => {
                return dayjs(params.value).format('L')
            },
            filterable: false
        },
        {
            field: 'end_date',
            headerName: t('settings.paycodes_combination.table.columns.end_date'),
            flex: 1,
            renderCell: (params) => {
                return dayjs(params.value).format('L')
            },
            filterable: false
        },
        {
            field: 'paycodes',
            headerName: t('settings.paycodes_combination.table.columns.paycodes'),
            flex: 1,
            renderCell: (params) => {
                if (params.value.length > 0) {
                    return <Typography sx={{m:2}}variant="subtitle2" gutterBottom>{params.value.length}</Typography>
                }else {
                    return <Typography sx={{m:2}} variant="subtitle2" gutterBottom>0</Typography>
                }
            },
            align: 'center',
            filterable: false

        },
        {
            field: 'enable',
            headerName: t('settings.paycodes_combination.table.columns.enable'),
            flex: 1,
            renderCell: (params) => {
                if (params.value) {
                    return <Typography sx={{m:2}}variant="subtitle2" gutterBottom>{t('settings.paycodes_combination.table.columns.enable')}</Typography>
                }else{
                    return <Typography sx={{m:2}} variant="subtitle2" gutterBottom>{t('settings.paycodes_combination.table.columns.disable')}</Typography>
                }
             },
             filterable: false
        }
    ]

    const reloading = () => {
        if(JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.GetPaycodesCombinations')){
            gettingPycodesCombination(setLoadingPaycodesCombination, setRows)
        }else{
            setRows([])
            setLoadingPaycodesCombination(false)
        }
    }

    const cleanSelected = () => {
        setSelectedRow([])
        setSelected([])
    }

    useEffect(() => {
        reloading(setLoadingPaycodesCombination, setRows)
    }, [])

    return (
        <>
            <div
                style={{width: '100%', height: '100%', }}
                onContextMenu={(event) => {
                    event.preventDefault();
                    setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6} : null)
                }}
            >
                <DataGridPro
                    columns={columns}
                    rows={rows}
                    showCellVerticalBorder
                    showColumnVerticalBorder
                    headerFilters
                    localeText={
                        {
                            headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                            headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                            headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                            headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                            headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                            headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                            footerRowSelected: (count) =>
                                count > 1
                                ? t('general.table.footerFilters.footerRowSelected.>1', {count: count.toLocaleString()})
                                : t('general.table.footerFilters.footerRowSelected.=1', {count: count.toLocaleString()}),

                            footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                            MuiTablePagination: {
                                labelDisplayedRows: ({ from, to, count }) =>
                                    t('general.table.footerFilters.labelDisplayedRows', {from: from.toLocaleString(), to: to.toLocaleString(), count: count.toLocaleString()}),
                                labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),},
                        }
                    }
                    disableColumnMenu
                    loading={loadingPaycodesCombination}
                    checkboxSelection
                    pagination
                    onRowSelectionModelChange={(newRowSelectionModel) => {
                        let data = rows.filter((row) => newRowSelectionModel.includes(row.id))
                        setSelected(newRowSelectionModel);
                        setSelectedRow(data);
                    }}
                    rowSelectionModel={selected}
                    sx={{
                        height: 'calc(100vh - 140px)',
                    }}
                />

                <ContextMenu
                    contextMenu={contextMenu}
                    setContextMenu={setContextMenu}
                    selectedRow={selectedRow}
                    reloading={reloading}
                    onOpenAdd={handleOpenAdd}
                    onOpenEdit={handleOpenEdit}
                    onOpenInfo={handleOpenInfo}
                />

                <AddPaycodesCombination open={openAdd} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} reloading={reloading} rows={rows} />
                <EditPaycodesCombination
                    open={openEdit}
                    setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }}
                    selectedRow={selectedRowsForRoute}
                    setSelected={cleanSelected}
                    reloading={reloading}
                    rows={rows}
                />
                <InfoPaycodesCombination
                    open={openInfo}
                    setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }}
                    selectedRow={selectedRowsForRoute}
                />

            </div>
        </>
    )
}

export {PaycodesCombinationTable}